
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

export interface Recipe {
  title?: string;
  ingredients?: string[];
  instructions?: string[];
  imageUrl?: string;
  prepTime?: string;
  cookTime?: string;
  temperature?: string;
}

export class FirecrawlService {
  static async getApiKey(): Promise<string | null> {
    const { data, error } = await supabase
      .from('secrets')
      .select('value')
      .eq('name', 'FIRECRAWL_API_KEY')
      .maybeSingle();
    
    if (error) {
      console.error('Error fetching Firecrawl API key:', error);
      return null;
    }
    
    return data?.value || null;
  }

  static async scrapeRecipe(url: string): Promise<Recipe> {
    try {
      console.log('Invoking Edge Function to scrape recipe:', url);
      
      // Add a timeout to prevent indefinite waiting
      const timeoutDuration = 40000; // 40 seconds timeout
      
      const fetchPromise = supabase.functions.invoke('scrape-recipe', {
        body: { url }
      });
      
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error('Recipe scraping timed out. Please try again or use a different recipe URL.'));
        }, timeoutDuration);
      });
      
      // Race between fetch and timeout
      const response = await Promise.race([fetchPromise, timeoutPromise]) as any;
      
      if (response.error) {
        console.error('Edge Function error:', response.error);
        throw new Error(response.error.message || 'Failed to scrape recipe');
      }

      console.log('Edge Function response:', response.data);

      if (!response.data?.data) {
        throw new Error('No recipe data returned');
      }

      // Validate the data
      const recipeData = response.data.data;
      if (!recipeData.title || !recipeData.title.trim()) {
        throw new Error('Could not extract title from this recipe. Please try a different recipe URL.');
      }
      
      if (!recipeData.ingredients || recipeData.ingredients.length === 0) {
        throw new Error('Could not extract ingredients from this recipe. Please try a different recipe URL.');
      }

      if (!recipeData.instructions || recipeData.instructions.length === 0) {
        throw new Error('Could not extract cooking instructions from this recipe. Please try a different recipe URL.');
      }

      // Handle the response data structure and ensure arrays
      const recipe: Recipe = {
        title: recipeData.title?.trim() || 'Untitled Recipe',
        ingredients: Array.isArray(recipeData.ingredients) 
          ? recipeData.ingredients.filter(i => i && i.trim())
          : typeof recipeData.ingredients === 'string'
          ? [recipeData.ingredients]
          : [],
        instructions: Array.isArray(recipeData.instructions)
          ? recipeData.instructions.filter(i => i && i.trim())
          : typeof recipeData.instructions === 'string'
          ? [recipeData.instructions]
          : [],
        imageUrl: recipeData.imageUrl || null,
        prepTime: recipeData.prepTime,
        cookTime: recipeData.cookTime,
        temperature: recipeData.temperature
      };

      // Additional validation to ensure non-empty arrays
      if (recipe.ingredients.length === 0) {
        throw new Error('No valid ingredients found. Please try a different recipe.');
      }
      
      if (recipe.instructions.length === 0) {
        throw new Error('No valid instructions found. Please try a different recipe.');
      }

      return recipe;
    } catch (error) {
      console.error('Error in scrapeRecipe:', error);
      throw error;
    }
  }
}
